import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './index.css';
import { AuthProvider } from './AuthContext';
import ProtectedRoute from './ProtectedRoute';
import Login from './Login';
import CompanyActivation from './CompanyActivation';
import Signup from './Signup';
import ChangePassword from './ChangePassword';
import Sidebar from './SidebarModern';
import MarketingHub from './MarketingHub';
import DesignHub from './DesignHub';
import DocumentTemplatesPage from './DocumentTemplatesPage';
import PdfOverlayTemplatesPage from './PdfOverlayTemplatesPage';
import FormDesignerPage from './FormDesignerPage';

import CampaignPage from './CampaignPage';
import BlogPostsPage from './BlogPostsPage';
import ContactManager from './ContactManager';
import MarketingMasterTemplates from './MarketingMasterTemplates';
import MarketingImageLibrary from './MarketingImageLibrary';
import TopicsPage from './TopicsPage';
import MarketingAnalytics from './MarketingAnalytics';
import Dashboard from './Dashboard';
import AdminLanding from './AdminLanding';
import SettingsLanding from './SettingsLanding';
import ReportsAndTemplates from './ReportsAndTemplates';
import Reports from './Reports';
import ReportBuilder from './ReportBuilder';
import ReportViewer from './ReportViewer';
import EmailTemplates from './EmailTemplates';
import EmailTemplateDetail from './EmailTemplateDetail';
import NoteTemplatesPage from './NoteTemplatesPage';
import TemplateLibraryPage from './TemplateLibraryPage';
import TemplateBuilder from './TemplateBuilder';
import Employees from './Employees';
import EmployeeDetail from './EmployeeDetail';
import Doors from './modules/door_draw/pages/Doors';
import DoorDesignerHub from './modules/door_draw/pages/DoorDesignerHub';
import DoorDesignerConfigure from './modules/door_draw/pages/DoorDesignerConfigure';
import DoorFabrication from './modules/door_draw/pages/DoorFabrication';
import DoorPurchaseOrders from './modules/door_draw/pages/DoorPurchaseOrders';
import DoorDrawingLibrary from './modules/door_draw/pages/DoorDrawingLibrary';
import KestrelCuts from './modules/door_draw/src/pages/KestrelCuts';
// import DoorConfiguratorPage from './modules/door_draw/pages/DoorConfiguratorPage';
import CrmRoutes from './routes/CrmRoutes';
import OpsRoutes from './routes/OpsRoutes';
import FinanceRoutes from './routes/FinanceRoutes';
import Invoices from './modules/finance/pages/Invoices';
import InvoiceDetail from './modules/finance/pages/InvoiceDetail';
import QuoteDetail from './modules/finance/pages/QuoteDetail';
import PpmRoutes from './routes/PpmRoutes';
import RamsRoutes from './routes/RamsRoutes';
import ReportRoutes from './routes/ReportRoutes';
import SettingRoutes from './routes/SettingRoutes';
import CustomerDetail from './modules/crm/pages/CustomerDetailModern';
import SiteDetail from './SiteDetailModern';
import SupplierDetail from './modules/ops/pages/SupplierDetail';
import JobDetail from './modules/ops/pages/JobDetail';
import JobAppointmentsPage from './modules/ops/pages/JobAppointmentsPage';
import JobMaterialsPage from './modules/ops/pages/JobMaterialsPage';
import JobRamsPage from './modules/ops/pages/JobRamsPage';
import SalesOrderDetail from './modules/crm/pages/SalesOrderDetail';
import PurchaseOrdersDashboard from './PurchaseOrdersDashboard';
import PurchaseOrderNew from './PurchaseOrderNew';
import PurchaseOrderDetail from './PurchaseOrderDetail';
import ProductDetail from './ProductDetail';
import LabourCostDetail from './LabourCostDetail';
import ConsumableDetail from './ConsumableDetail';
import DatabaseSetup from './DatabaseSetup';
import VersionChecker from './VersionChecker';

function App() {
  const [dbReady, setDbReady] = useState(null);
  const [checkingDb, setCheckingDb] = useState(true);

  useEffect(() => {
    console.log('%c🚪 DoorBase v1.12.7', 'color: #2563eb; font-size: 18px; font-weight: bold');
    console.log('%cWhite sidebar with modern icon grid layout', 'color: #7c3aed');
    checkDatabaseStatus();
  }, []);

  const checkDatabaseStatus = async () => {
    setDbReady(true);
    setCheckingDb(false);
  };

  if (checkingDb) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading DoorBase...</p>
        </div>
      </div>
    );
  }

  if (!dbReady) {
    return <DatabaseSetup onComplete={() => setDbReady(true)} />;
  }

  return (
    <AuthProvider>
      <Router>
        <VersionChecker />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/activate-company" element={<CompanyActivation />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/change-password" element={<ChangePassword />} />

          <Route path="/*" element={
            <ProtectedRoute>
              <div className="min-h-screen bg-gray-50">
                <Sidebar />
                <div className="ml-56">
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/admin" element={<AdminLanding />} />
                    <Route path="/analytics/*" element={<ReportRoutes />} />
                    <Route path="/crm/*" element={<CrmRoutes />} />
                    <Route path="/ops/*" element={<OpsRoutes />} />
                    <Route path="/finance/*" element={<FinanceRoutes />} />
                    <Route path="/ppm/*" element={<PpmRoutes />} />
                    <Route path="/rams/*" element={<RamsRoutes />} />
                    <Route path="/marketing-hub" element={<MarketingHub />} />
                    <Route path="/marketing-hub/campaigns" element={<CampaignPage />} />
                    <Route path="/marketing-hub/blog-posts" element={<BlogPostsPage />} />
                    <Route path="/marketing-hub/contacts" element={<ContactManager />} />
                    <Route path="/marketing-hub/templates" element={<MarketingMasterTemplates />} />
                    <Route path="/marketing-hub/images" element={<MarketingImageLibrary />} />
                    <Route path="/marketing-hub/topics" element={<TopicsPage />} />
                    <Route path="/marketing-hub/analytics" element={<MarketingAnalytics />} />

                    {/* Design Hub */}
                    <Route path="/design-hub" element={<DesignHub />} />
                    <Route path="/design-hub/document-templates" element={<DocumentTemplatesPage />} />
                    <Route path="/design-hub/pdf-templates" element={<PdfOverlayTemplatesPage />} />
                    <Route path="/design-hub/form-designer" element={<FormDesignerPage />} />
                    <Route path="/settings/*" element={<SettingRoutes />} />

                    {/* Restored admin module routes */}
                    <Route path="/settings-landing" element={<SettingsLanding />} />
                    <Route path="/reports-and-templates" element={<ReportsAndTemplates />} />
                    <Route path="/reports" element={<Reports />} />
                    <Route path="/reports/new" element={<ReportBuilder />} />
                    <Route path="/reports/:id/edit" element={<ReportBuilder />} />
                    <Route path="/reports/:id/view" element={<ReportViewer />} />
                    <Route path="/note-templates" element={<NoteTemplatesPage />} />
                    <Route path="/template-library" element={<TemplateLibraryPage />} />
                    <Route path="/settings/templates" element={<TemplateLibraryPage />} />
                    <Route path="/settings/templates/new" element={<TemplateBuilder />} />
                    <Route path="/settings/templates/:id" element={<TemplateBuilder />} />
                    <Route path="/email-templates" element={<EmailTemplates />} />
                    <Route path="/email-templates/new" element={<EmailTemplateDetail />} />
                    <Route path="/email-templates/:id" element={<EmailTemplateDetail />} />
                    <Route path="/employees" element={<Employees />} />
                    <Route path="/employees/new" element={<EmployeeDetail />} />
                    <Route path="/employees/:id" element={<EmployeeDetail />} />
                    <Route path="/door-designer/drawings" element={<DoorDrawingLibrary />} />

                    {/* Legacy aliases kept for links created before the route refactor */}
                    <Route path="/daily-planning" element={<Navigate to="/ops/daily-planning" replace />} />
                    <Route path="/engineer-dispatch" element={<Navigate to="/ops/engineer-dispatch" replace />} />
                    <Route path="/fleet-tracking" element={<Navigate to="/ops/fleet-tracking" replace />} />
                    <Route path="/customers/:id" element={<CustomerDetail />} />
                    <Route path="/sites/:id" element={<SiteDetail />} />
                    <Route path="/suppliers/:id" element={<SupplierDetail />} />
                    <Route path="/jobs/:id" element={<JobDetail />} />
                    <Route path="/jobs/:id/appointments" element={<JobAppointmentsPage />} />
                    <Route path="/jobs/:id/materials" element={<JobMaterialsPage />} />
                    <Route path="/jobs/:id/rams" element={<JobRamsPage />} />
                    <Route path="/sales-orders/new" element={<SalesOrderDetail />} />
                    <Route path="/sales-orders/:id" element={<SalesOrderDetail />} />
                    <Route path="/purchase-orders/dashboard" element={<PurchaseOrdersDashboard />} />
                    <Route path="/purchase-orders/new" element={<PurchaseOrderNew />} />
                    <Route path="/purchase-orders/:id" element={<PurchaseOrderDetail />} />
                    <Route path="/products/:id" element={<ProductDetail />} />
                    <Route path="/labour-costs/:id" element={<LabourCostDetail />} />
                    <Route path="/consumables/:id" element={<ConsumableDetail />} />
                    <Route path="/invoices" element={<Invoices />} />
                    <Route path="/invoices/:id" element={<InvoiceDetail />} />
                    <Route path="/quotes" element={<Navigate to="/ops/quotes" replace />} />
                    <Route path="/quotes/:id" element={<QuoteDetail />} />

                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </div>
              </div>
            </ProtectedRoute>
          } />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

function ComingSoon({ title, icon }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="text-8xl mb-4">{icon}</div>
        <h1 className="text-4xl font-bold text-gray-800 mb-2">{title}</h1>
        <p className="text-xl text-gray-600">Coming Soon</p>
        <p className="text-gray-500 mt-4">This module is being built!</p>
      </div>
    </div>
  );
}

export default App;
